import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import { Link } from "react-router-dom";
import DesktopHeroDisplay from "@/components/landing/DesktopHeroDisplay";
import HeroSystemPreview from "@/components/landing/HeroSystemPreview";
import MapleOSLogo from "@/components/common/MapleOSLogo";
import ArchitectureFlowDialog from "@/components/landing/ArchitectureFlowDialog";
import { Button } from "@/components/ui/button";

const valuePills = ["human-first", "real-time reasoning", "modular execution"];

export default function HeroLivingSystem() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section id="welcome-to" className="relative overflow-hidden px-6 pb-20 pt-8 md:pb-28 md:pt-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(132,255,76,0.14),transparent_28%),linear-gradient(180deg,#04070b_0%,#05080d_44%,#060a10_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:36px_36px] opacity-20" />
      <div className="absolute left-1/2 top-0 h-[24rem] w-[24rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />

      {!shouldReduceMotion && (
        <>
          <motion.div
            aria-hidden="true"
            animate={{ opacity: [0.35, 0.65, 0.35], scale: [0.96, 1.08, 0.96] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-[14%] top-[16rem] h-48 w-48 rounded-full bg-primary/8 blur-3xl"
          />
          <motion.div
            aria-hidden="true"
            animate={{ opacity: [0.2, 0.45, 0.2], scale: [1, 1.12, 1] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="absolute right-[12%] top-[22rem] h-56 w-56 rounded-full bg-emerald-400/8 blur-3xl"
          />
        </>
      )}

      <div className="relative mx-auto flex w-full max-w-[92rem] flex-col items-center text-center">
        <h1 className="text-balance text-5xl font-semibold tracking-[-0.065em] text-white sm:text-6xl lg:text-7xl xl:text-[5.6rem] xl:leading-[0.94]">
          <span>Welcome to </span>
          <span className="relative inline-flex items-center align-middle">
            <span className="absolute inset-x-6 top-1/2 h-[0.62em] -translate-y-1/2 rounded-full bg-[hsl(75_100%_42%_/_0.08)] blur-2xl" aria-hidden="true" />
            <span className="absolute inset-x-14 top-1/2 h-[0.28em] -translate-y-1/2 rounded-full bg-[hsl(75_100%_42%_/_0.12)] blur-lg" aria-hidden="true" />
            <span className="sr-only">mapleos</span>
            <MapleOSLogo
              textOnly
              aria-hidden="true"
              className="relative z-10 h-[0.78em] w-auto text-[hsl(75_100%_42%)] drop-shadow-[0_0_12px_hsla(75,100%,42%,0.38)] sm:drop-shadow-[0_0_20px_hsla(75,100%,42%,0.52)]"
            />
          </span>
        </h1>

        <p className="mx-auto mt-6 max-w-3xl text-base leading-7 text-white/65 sm:text-lg md:text-xl md:leading-8">
            We know you're tired of big tech trying to replace you, inserting AI <i>everywhere</i>. 
            They no longer value your <i>trust</i>. We built mapleos to help you take back your control.
        </p>

        <div id="hero-slider" className="mt-14 w-full scroll-mt-24 md:mt-16">
          <DesktopHeroDisplay />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-5xl"
        >
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-full px-6 text-sm font-semibold shadow-[0_0_28px_hsl(var(--primary)/0.28)]">
              <Link to="/start" aria-label="Launch mapleos">
                Launch MapleOS <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 rounded-full border-white/15 bg-white/5 px-6 text-sm font-semibold text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/downloads" aria-label="Download MapleOS">
                Download OS <Download className="h-4 w-4" />
              </Link>
            </Button>
            <ArchitectureFlowDialog>
              <Button size="lg" variant="ghost" className="h-12 rounded-full px-4 text-sm font-semibold text-white/80 hover:bg-white/5 hover:text-white">
                View Architecture
              </Button>
            </ArchitectureFlowDialog>
          </div>

          <p className="mx-auto mt-6 max-w-3xl text-base leading-7 text-white/65 sm:text-lg md:text-xl md:leading-8">
            Built for work and play. A living system for your mind.
            <br />
            Everything runs in one ecosystem.
          </p>
        </motion.div>
      </div>
    </section>
  );
}